/**
 * Sync events serveur ↔ local — Phase 4, incrément 4.6.
 *
 * Étend la stratégie ADR 0009 (server-wins) aux events, première
 * sub-entité d'un foyer livrée côté API. Le flux :
 *
 *   1. Pour chaque foyer **côté serveur** (id UUID + myRole connu) :
 *      a. Migrer les events locaux pré-auth (id `ev_<…>`) via POST.
 *      b. GET `/homes/:homeId/events` → autorité finale.
 *      c. `applyEventsSyncResult(homeId, { idMap, serverEvents })`
 *         applique le merge atomiquement pour ce home.
 *
 *   2. Les foyers locaux non-UUID (non encore migrés par
 *      `homes-sync`) sont skipés — leurs events seront pris au
 *      prochain cycle, après que le home soit migré.
 *
 * Déclenchement : appelé par `homes-sync.ts` à la fin d'un sync
 * homes réussi. Pas de déclencheur indépendant en 4.6 — la
 * cohérence du chaînage évite qu'events-sync tape l'API avec des
 * UUIDs qui n'existent pas encore.
 *
 * Mode local-only préservé : si l'API ne répond pas, on warn console
 * et le state local reste utilisable.
 */
import * as eventsApi from '../api/events.js';
import { ApiError } from '../api/client.js';
import {
  applyEventsSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerEventPayload,
} from './homes.js';
import type { ClassicHomeData, FamilyEvent } from '@nestor/shared-types';

export interface EventsSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: EventsSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getEventsSyncStatus(): EventsSyncStatus {
  return status;
}

/**
 * Synchronise les events pour tous les foyers connus du serveur.
 * Idempotent (dédup via `inFlight`).
 *
 * Stratégie par foyer :
 *
 *   1. Migration POST des events locaux pré-auth.
 *   2. GET liste serveur.
 *   3. `applyEventsSyncResult(homeId, …)` atomique.
 *
 * Erreurs ponctuelles (POST individuel raté, 1 GET échoue) : warn
 * console, on continue les autres homes. Erreurs globales (auth
 * down sur tous les homes) : status passe à `'error'`, state local
 * intact.
 */
export async function syncEventsForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  // Itère sur les foyers connus du serveur (UUID) en mode classique
  // (events n'existe pas en coparenting — `activities` à la place).
  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'classic') continue;

    const data = home.data as ClassicHomeData;

    // 1. Migration POST des events locaux pré-auth.
    //
    // Phase 3.3.2 : `createEvent` retourne désormais `SendResult` —
    // `kind: 'sent'` = on remap l'id local ; `kind: 'queued'` =
    // réseau KO, on garde le local id pour le retry au prochain
    // online event (le replay-engine relancera POST tout seul et
    // le prochain sync verra le nouvel id côté serveur).
    const idMap = new Map<string, string>();
    const localOnly = data.events.filter((e) => !isServerHomeId(e.id));
    for (const event of localOnly) {
      try {
        // Phase 3.3.7 : passe le localId pour que le replay engine
        // émette automatiquement le remap event au success. Quand
        // online + 201, le store domain a déjà l'idMap appliqué
        // par la sync ci-après, donc le remap event ne fait que
        // confirmer (idempotent). Quand offline → queued, le replay
        // futur remappera quand le réseau reviendra.
        const result = await eventsApi.createEvent(home.id, buildCreateInput(event), event.id);
        if (result.kind === 'sent') {
          idMap.set(event.id, result.data.event.id);
        }
        // kind === 'queued' : pas de remap, le local id reste tel
        // quel. La prochaine sync (déclenchée par online event)
        // re-tentera la migration via la queue.
      } catch (err) {
        // ApiError (4xx/5xx serveur) — le serveur a tranché,
        // pas d'enqueue. On log et on continue avec les autres.
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[events-sync] migration POST échouée', {
          homeId: home.id,
          eventId: event.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply pour ce home.
    try {
      const serverEvents = await eventsApi.listEvents(home.id);
      const payload: ServerEventPayload[] = serverEvents.map((e) => ({
        id: e.id,
        title: e.title,
        date: e.date,
        time: e.time,
        location: e.location,
      }));
      applyEventsSyncResult(home.id, { idMap, serverEvents: payload });
    } catch (err) {
      // Si on a migré des events sans pouvoir fetch, on applique au
      // moins le remap pour ne pas perdre les ids serveur retournés.
      if (idMap.size > 0) {
        applyEventsSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[events-sync] fetch /events échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(event: FamilyEvent): eventsApi.CreateEventInput {
  return {
    title: event.title,
    date: event.date,
    ...(event.time ? { time: event.time } : {}),
    ...(event.location ? { location: event.location } : {}),
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/**
 * @internal pour tests — reset le state module-level.
 */
export function _resetEventsSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
